// @flow
import { partition } from "ramda/src"

import type { LanguageInfo } from "common/records/LanguageInfo"
import type { Translations } from "common/services/intl/pureTranslate"
import { distance } from "common/services/formatters/formatters"
import MultiPlace from "common/records/MultiPlace"
import SearchPlace from "common/records/SearchPlace"
import Place from "common/records/Place"
import Radius from "common/records/Radius"
import { PLACE_VALUE_MODES, PLACE_MODE_KEYS, PLACE_TYPES } from "../../consts/place"
import { TEXT_LENGTH } from "common/consts/textLength"
import pureTranslate from "../intl/pureTranslate"

type TranslatePlace = MultiPlace | SearchPlace | Place | Radius

type Context = { field?: string }

type ShortContext = { field?: string, maxLength?: number, fullLength?: boolean }

type DisplayContext = {
  radiusListLength?: number,
  searchedText?: string,
  pickerRowLevel?: number,
}

export const getShortName = (place: Place): string => {
  switch (place.get("type")) {
    case PLACE_TYPES.CITY:
      return place.get("value").replace(/\s*\(.+\)/, "")
    case PLACE_TYPES.AIRPORT:
      return `${place.city ? place.city.name : ""} ${place
        .get("value")
        .replace(/.+\s/, "")
        .replace(/\(/, "")
        .replace(/\)/, "")}`
    default:
      return place.get("value")
  }
}

export const getUrlString = (place: TranslatePlace) => {
  switch (place.constructor) {
    case MultiPlace:
      return place.places.size > 0 ? place.places.map(getUrlString).join(",") : "-"
    case SearchPlace: {
      switch (place.mode) {
        case PLACE_VALUE_MODES.PLACE:
        case PLACE_VALUE_MODES.RADIUS:
          if (place.value instanceof Radius) {
            return getUrlString(place.value)
          }
          if (place.value instanceof Place) {
            return getUrlString(place.value)
          }
          return ""
        case PLACE_VALUE_MODES.ID:
        case PLACE_VALUE_MODES.TEXT:
          return place.value

        case PLACE_VALUE_MODES.ANYWHERE:
          return place.mode

        case PLACE_VALUE_MODES.UNSELECTED:
          return "-"

        default:
          return ""
      }
    }
    case Place:
      return place.slug
    case Radius: {
      if (place instanceof Radius) {
        if (place.closeCity) {
          // brno-czech-republic-100km
          return `${getUrlString(place.closeCity)}-${place.radius}km`
        }
        return `${place.lat}-${place.lng}-${place.radius}km`
      }
      return ""
    }
    default:
      return ""
  }
}

export const getCountryName = (place: Place): string => {
  if (place.country && place.country.name) {
    return place.country.name
  }

  // TODO: I have a feeling that this is a dead code, or it should be filled when receiving the data from server, not here
  const matches = place.value.match(/\s*\((.+)\)/)
  return matches && matches[1] ? matches[1] : ""
}

export const getCityName = (place: SearchPlace | Place): string => {
  if (place.constructor === SearchPlace) {
    if (place.mode !== PLACE_VALUE_MODES.PLACE || !place.value) {
      return ""
    }
    if (place.value instanceof Place) {
      switch (place.value.type) {
        case PLACE_TYPES.AIRPORT:
        case PLACE_TYPES.BUS_STATION:
        case PLACE_TYPES.TRAIN_STATION:
        case PLACE_TYPES.SPECIAL:
          return place.value.city ? place.value.city.name : ""
        case PLACE_TYPES.CITY:
          return getShortName(place.value)
        default:
          return ""
      }
    }
    return ""
  }

  // beware before using, we are stripping LRT markers because they are not needed
  // with current usage and causing problem when rendered on map
  switch (place.type) {
    case PLACE_TYPES.AIRPORT:
    case PLACE_TYPES.BUS_STATION:
    case PLACE_TYPES.TRAIN_STATION:
      return place.city ? place.city.name : ""
    default:
      return place
        .get("value")
        .replace(/\s*\(.+\)/, "")
        .replace(/[\u200e]/g, "")
  }
}

export default class PlaceFormatter {
  translations: Translations

  languageInfo: LanguageInfo

  cities: Map<string, Place>

  constructor(translations: Translations, languageInfo: LanguageInfo, cities: Map<string, Place>) {
    this.translations = translations
    this.languageInfo = languageInfo
    this.cities = cities
  }

  _translate = (key: string, params?: { [key: string]: string }) =>
    pureTranslate(key, params, { translations: this.translations })

  getText = (translatePlace: TranslatePlace, context: Context = {}): string => {
    switch (translatePlace.constructor) {
      case MultiPlace:
        return translatePlace.places.map(this.getText).join(" ")
      case SearchPlace: {
        switch (translatePlace.mode) {
          case PLACE_VALUE_MODES.ID:
          case PLACE_VALUE_MODES.TEXT:
            return translatePlace.value

          case PLACE_VALUE_MODES.PLACE:
            // the value is a Place, and it has a value
            if (translatePlace.value instanceof Place) {
              return translatePlace.value.value
            }
            return ""
          case PLACE_VALUE_MODES.RADIUS:
            if (translatePlace.value instanceof Radius) {
              return this.getText(translatePlace.value)
            }
            return ""

          case PLACE_VALUE_MODES.ANYWHERE:
            return this._translate(PLACE_MODE_KEYS[translatePlace.mode])

          case PLACE_VALUE_MODES.UNSELECTED:
            if (context.field) {
              return this._translate(
                context.field === "origin"
                  ? __("form_search.from_where")
                  : __("form_search.to_where"),
              )
            }
            return "Unselected"

          default:
            return ""
        }
      }
      case Place:
        return translatePlace.get("value")
      case Radius:
        if (translatePlace instanceof Radius) {
          if (translatePlace.closeCity) {
            return `${getShortName(translatePlace.closeCity)} +${distance(
              this.languageInfo,
              translatePlace.radius,
            )}`
          }
          return `${translatePlace.lat}, ${translatePlace.lng} (${distance(
            this.languageInfo,
            translatePlace.radius,
          )})`
        }
        return ""
      default:
        return ""
    }
  }

  getShortText = (translatePlace: TranslatePlace, context: ?ShortContext): string => {
    switch (translatePlace.constructor) {
      case MultiPlace: {
        const placesString = translatePlace.places
          .map(place =>
            place.mode === PLACE_VALUE_MODES.RADIUS
              ? this.getCityListText(
                  place.value,
                  context && context.fullLength ? 0 : TEXT_LENGTH.RADIUS.RECENT_LENGTH,
                )
              : this.getShortText(place, {
                  maxLength: context && context.fullLength ? 0 : TEXT_LENGTH.BASIC_LENGTH,
                }),
          )
          .join(", ")

        const newLength =
          context && context.maxLength ? context.maxLength : TEXT_LENGTH.RECENT_SEARCH.BASIC_LENGTH

        return (context && context.fullLength) || translatePlace.places.count() === 1
          ? placesString
          : placesString
              .substring(0, newLength)
              .concat(newLength < placesString.length ? "..." : "")
      }
      case SearchPlace: {
        switch (translatePlace.mode) {
          case PLACE_VALUE_MODES.ID:
          case PLACE_VALUE_MODES.TEXT:
            return translatePlace.value

          case PLACE_VALUE_MODES.PLACE: {
            // the value is a Place, and it has a value
            if (translatePlace.value instanceof Place) {
              const text = getShortName(translatePlace.value)
              return context && context.maxLength && text.length >= context.maxLength + 2
                ? `${text.substr(0, context.maxLength)}...`
                : text
            }
            return ""
          }
          case PLACE_VALUE_MODES.RADIUS: {
            if (translatePlace.value instanceof Radius) {
              const text = this.getText(translatePlace.value)
              return context && context.maxLength && text.length >= context.maxLength + 2
                ? `${text.substr(0, context.maxLength)}...`
                : text
            }
            return ""
          }
          case PLACE_VALUE_MODES.ANYWHERE:
            return this._translate(PLACE_MODE_KEYS[translatePlace.mode])

          // TODO: use translation UNSELECTED
          case PLACE_VALUE_MODES.UNSELECTED:
            if (context && context.field) {
              return this._translate(
                context.field === "origin"
                  ? __("form_search.from_where")
                  : __("form_search.to_where"),
              )
            }
            return "Unselected"

          default:
            return ""
        }
      }
      case Radius:
        if (translatePlace instanceof Radius) {
          if (translatePlace.closeCity) {
            return `${this.getShortText(translatePlace.closeCity)}`
          }
          return `${Math.round(translatePlace.lat)}, ${Math.round(translatePlace.lng)} (${distance(
            this.languageInfo,
            translatePlace.radius,
          )})`
        }
        return ""
      case Place: {
        switch (translatePlace.get("type")) {
          case PLACE_TYPES.CITY:
            return translatePlace.get("value").replace(/\s*\(.+\)/, "")
          case PLACE_TYPES.AIRPORT:
            return `${translatePlace.city ? translatePlace.city.name : ""} ${translatePlace
              .get("value")
              .replace(/.+\s/, "")
              .replace(/\(/, "")
              .replace(/\)/, "")}`
          default:
            return translatePlace.get("value")
        }
      }
      default:
        return ""
    }
  }

  getDisplayName = (place: TranslatePlace, context?: DisplayContext) => {
    switch (place.constructor) {
      case SearchPlace:
        switch (place.mode) {
          case PLACE_VALUE_MODES.RADIUS:
            if (place.value instanceof Radius && context) {
              return this.getCityListText(
                place.value,
                context && context.radiusListLength,
                context && context.searchedText,
              )
            }
            // flow is s-t-u-p-i-d
            return ""
          case PLACE_VALUE_MODES.ANYWHERE:
            return this._translate(PLACE_MODE_KEYS[place.mode])
          case PLACE_VALUE_MODES.PLACE:
            if (place.value instanceof Place) return this.getDisplayName(place.value, context)
            // flow is s-t-u-p-i-d
            return ""
          default:
            return place.value
        }
      case Place:
        switch (place.type) {
          case PLACE_TYPES.AIRPORT:
          case PLACE_TYPES.BUS_STATION:
          case PLACE_TYPES.TRAIN_STATION:
            return !(context && context.pickerRowLevel)
              ? `${place.code} ${(place.city && place.city.name) || ""}, ${place.countryName ||
                  ""} - ${place.name || ""}`
              : `${place.code} ${place.name || ""}`
          case PLACE_TYPES.CITY:
            return `${getShortName(place)}, ${place.countryName || ""}`
          default:
            return getShortName(place)
        }
      case Radius:
      default:
        return ""
    }
  }

  getCityListText = (radius: Radius, length?: number, searchedText?: string): string => {
    const placesInRadius = radius
      .getPlacesInRadius(this.cities)
      .map(getShortName)
      .valueSeq()
      .toArray()
    const [including, rest] = partition(
      name => searchedText && name.toLowerCase().includes(searchedText.toLowerCase()),
      placesInRadius,
    )
    const text = including.concat(rest).join(", ") || this.getShortText(radius)
    return length && text.length > length + 2 ? `${text.substr(0, length)}...` : text
  }
}
